<script lang="ts">
  import { onMount } from 'svelte';
  import { parseAppRoute, routeLabel, type AppRoute } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Shell from '$lib/components/Shell.svelte';
  import LoginPage from '$lib/pages/LoginPage.svelte';
  import InvitePage from '$lib/pages/InvitePage.svelte';
  import DashboardPage from '$lib/pages/DashboardPage.svelte';
  import ClaimDevicePage from '$lib/pages/ClaimDevicePage.svelte';
  import RegisterDevicePage from '$lib/pages/RegisterDevicePage.svelte';
  import DevicesPage from '$lib/pages/DevicesPage.svelte';
  import DeviceDetailPage from '$lib/pages/DeviceDetailPage.svelte';
  import GroupsPage from '$lib/pages/GroupsPage.svelte';
  import GroupDetailPage from '$lib/pages/GroupDetailPage.svelte';
  import AuditPage from '$lib/pages/AuditPage.svelte';
  import StaffPage from '$lib/pages/StaffPage.svelte';
  import ClientsPage from '$lib/pages/ClientsPage.svelte';
  import AccountPage from '$lib/pages/AccountPage.svelte';
  import PlaceholderPage from '$lib/pages/PlaceholderPage.svelte';
  import SettingsPage from '$lib/pages/SettingsPage.svelte';
  import SkuManagerPage from '$lib/pages/SkuManagerPage.svelte';
  import ProfilesPage from '$lib/pages/ProfilesPage.svelte';
  import TenantsPage from '$lib/pages/TenantsPage.svelte';
  import OrganizationPage from '$lib/pages/OrganizationPage.svelte';
  import UpstreamFormatsPage from '$lib/pages/UpstreamFormatsPage.svelte';
  import ConnectionsPage from '$lib/pages/ConnectionsPage.svelte';
  import SupportAccessPage from '$lib/pages/SupportAccessPage.svelte';
  import DevicePoolsPage from '$lib/pages/DevicePoolsPage.svelte';
  import DevicePoolDetailPage from '$lib/pages/DevicePoolDetailPage.svelte';
  import IngressConnectionsPage from '$lib/pages/IngressConnectionsPage.svelte';
  import CommandSetsPage from '$lib/pages/CommandSetsPage.svelte';
  import OTAPage from '$lib/pages/OTAPage.svelte';

  let route = $state<AppRoute>(parseAppRoute(window.location.hash));

  function syncRoute() {
    route = parseAppRoute(window.location.hash);
  }

  const mustChangePassword = $derived(Boolean(session.user?.mustChangePassword));

  /** Force Account when password change is required. */
  $effect(() => {
    if (!session.user?.mustChangePassword) return;
    if (route.name === 'account') return;
    if (window.location.hash !== '#/account') {
      window.location.hash = '#/account';
    } else {
      route = parseAppRoute('#/account');
    }
  });

  onMount(() => {
    window.addEventListener('hashchange', syncRoute);
    void session.bootstrap();
    return () => window.removeEventListener('hashchange', syncRoute);
  });

  /** After forced password change + re-login, land on dashboard (not Account). */
  $effect(() => {
    if (!session.user || session.user.mustChangePassword) return;
    if (session.welcomeBanner == null) return;
    if (typeof window !== 'undefined' && window.location.hash !== '#/dashboard') {
      window.location.hash = '#/dashboard';
    }
  });

  const shellRoute = $derived(
    mustChangePassword
      ? 'account'
      : route.name === 'device' || route.name === 'claim-device' || route.name === 'register-device'
        ? 'devices'
        : route.name === 'group'
          ? 'groups'
          : route.name === 'sku-manager' ||
              route.name === 'profiles' ||
              route.name === 'tenants' ||
              route.name === 'organization' ||
              route.name === 'upstream-formats' ||
              route.name === 'connections' ||
              route.name === 'support-access'
            ? 'settings'
            : route.name === 'unknown'
              ? 'dashboard'
              : route.name,
  );
</script>

{#if session.status === 'loading'}
  <div class="boot">
    <div class="spinner" aria-hidden="true"></div>
    <p>Starting Unarvu Cloud Connect…</p>
  </div>
{:else if route.name === 'invite'}
  <InvitePage token={route.token} />
{:else if !session.user}
  <LoginPage />
{:else}
  <Shell route={shellRoute} forcePasswordChange={mustChangePassword}>
    {#if mustChangePassword || route.name === 'account'}
      <AccountPage forcePasswordChange={mustChangePassword} />
    {:else if route.name === 'dashboard'}
      <DashboardPage />
    {:else if route.name === 'devices'}
      <DevicesPage />
    {:else if route.name === 'claim-device'}
      <ClaimDevicePage />
    {:else if route.name === 'register-device'}
      <RegisterDevicePage />
    {:else if route.name === 'device'}
      {#key route.deviceId}
        <DeviceDetailPage deviceId={route.deviceId} />
      {/key}
    {:else if route.name === 'groups'}
      <GroupsPage />
    {:else if route.name === 'group'}
      {#key route.groupId}
        <GroupDetailPage groupId={route.groupId} />
      {/key}
    {:else if route.name === 'clients'}
      <ClientsPage />
    {:else if route.name === 'staff'}
      <StaffPage />
    {:else if route.name === 'ota'}
      <OTAPage />
    {:else if route.name === 'audit'}
      <AuditPage />
    {:else if route.name === 'settings'}
      <SettingsPage />
    {:else if route.name === 'sku-manager'}
      <SkuManagerPage />
    {:else if route.name === 'profiles'}
      <ProfilesPage />
    {:else if route.name === 'tenants'}
      <TenantsPage />
    {:else if route.name === 'organization'}
      <OrganizationPage />
    {:else if route.name === 'upstream-formats'}
      <UpstreamFormatsPage />
    {:else if route.name === 'connections'}
      <ConnectionsPage />
    {:else if route.name === 'support-access'}
      <SupportAccessPage />
    {:else if route.name === 'device-pools'}
      <DevicePoolsPage />
    {:else if route.name === 'device-pool'}
      <DevicePoolDetailPage poolId={route.poolId} />
    {:else if route.name === 'ingress-connections'}
      <IngressConnectionsPage />
    {:else if route.name === 'command-sets'}
      <CommandSetsPage />
    {:else}
      <PlaceholderPage
        title={routeLabel(route)}
        description="Unknown route"
        blurb="That page is not registered. Use the sidebar to navigate."
      />
    {/if}
  </Shell>
{/if}

<style>
  .boot {
    min-height: 100vh;
    display: grid;
    place-content: center;
    justify-items: center;
    gap: 0.75rem;
    color: var(--text-muted, #6b7280);
    background: var(--bg, #f3f4f6);
  }

  .spinner {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid var(--border, #e5e7eb);
    border-top-color: var(--accent, #2563eb);
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
